/**
 * Gameplay constants — shared verbatim between client prediction and server
 * authority. Anything here influences the round OUTCOME and therefore must be
 * identical on both sides. Render-only constants stay in the client.
 */

/**
 * Fixed simulation timestep. The whole authoritative sim advances in exact
 * multiples of this; the renderer interpolates the leftover. 120 Hz: the
 * Verlet rope is sensitive to step size, and a smaller step keeps client and
 * server integrations from diverging.
 */
export const SIM_DT = 1 / 120;

/** Legacy per-frame clamp (kept for the not-yet-migrated client loop). */
export const FRAME_DT_MAX = 1 / 30;

export const INITIAL_BALANCE = 100;
export const INITIAL_BET = 10;

export const CRASH_OFFSCREEN_PADDING = 220;

export const INITIAL_SWING_ANGLE = -0.55;

export const MIN_VISIBLE_WORLD_WIDTH = 720;
export const MIN_VISIBLE_WORLD_HEIGHT = 1080;

// Pendulum swing while Benji waits on a vine.
export const SWING = {
  gravity: 1500,
  damping: 0.035,
  targetAngle: 0.88,
  pumpRate: 4.4,
  pumpOmegaSoftness: 0.6,
};

// Sliding down a freshly caught vine (gravity-driven).
export const SLIDE = {
  gravity: 1500,
  startSpeed: 150,
  maxSpeed: 350,
};

export const FLY = {
  gravity: 1500,
};

// Sprite body tilt. Most of these only drive the visible lean / seam easing
// (cosmetic), but they live here so client and server agree on the logical
// body angle exposed in snapshots.
export const BODY = {
  flyTiltScale: 0.75,
  flyForwardLean: 0.12,
  flyTiltLimit: 0.61,
  flySmoothing: 9,
  hangTiltScale: 0.72,
  hangTiltLimit: 0.72,
  slideToHangBlend: 0.16,
  gripRotationBlend: 0.14,
  poseBlend: 0.2,
};

export const LAUNCH = {
  referenceSpeed: 760,
  kickBase: 250,
  kickStrength: 280,
  maxSpeed: 1680,
};

export const CATCH = {
  radius: 44,
  missGraceX: 118,
  energyLoss: 0.78,
  snapChance: 0.05,
  gripBlend: 0.14,
  ropeImpulse: 0.018,
};

export const VINES = {
  bufferSize: 6,
  pivotY: 0,
  minHeight: 520,
  maxHeight: 780,
  spacing: { min: 570, max: 670 },
};

// Verlet rope gravity (was the default arg of Rope.simulate).
export const ROPE = {
  gravity: 900,
  damping: 0.988,
  constraintIterations: 10,
  windAmplitude: 14,
  windFrequency: 1.1,
};

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

// Render-only: not part of the authoritative sim, but kept with the other
// constants so the client has a single import site.
export const BENJI_SPRITE_SCALE = 0.22;

export const CAMERA = {
  benjiScreenXFraction: 0.34,
  portraitBenjiScreenXFraction: 0.22,
  waitingLerp: 3.0,
  jumpingLerp: 5.5,
  cullLeftViewFraction: 0.7,
  bgScroll: {
    idle: 30,
    waiting: 20,
    jumping: 160,
    cashed: 60,
    crashed: 20,
  },
};
